import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import { hzAlignmentZodSchema } from "@@/shared/pages/alignments-and-placements";
import { contentAutoPageBlockLayoutTypeZodSchema } from "@@/shared/pages/page-blocks/content/content-auto-block/content-auto-block-layout";
import { contentAutoPageBlockStyleTypeZodSchema } from "@@/shared/pages/page-blocks/content/content-auto-block/content-auto-block-style";
import { storageItemImageIdZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import { storageItemImageReferenceZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-reference";
import { hexColorZodSchema } from "@@/shared/stylable/hex-color/hex-color";
import {
    asTranslatableValue,
    translatableZodSchema,
} from "@@/shared/translations/translation";
import { faCubes } from "@fortawesome/pro-solid-svg-icons";
import type { Except } from "type-fest";
import { z } from "zod";
import { contentIdZodSchema } from "../../../content/content-id";
import { pageBlockBaseZodSchema } from "../../page-block-base";
import { pageBlockIdFactory } from "../../page-block-id";
import { blockContentSizeZodSchema } from "../shared/block-content-size";

const contentAutoPageBlockZodSchema = pageBlockBaseZodSchema.merge(
    z.object({
        type: z.literal("CONTENT_AUTO_PAGE_BLOCK"),

        // Texts
        title: translatableZodSchema,
        titleColor: hexColorZodSchema.optional(), // summer 2024
        subtitle: translatableZodSchema,
        subtitleColor: hexColorZodSchema.optional(), // summer 2024
        kicker: translatableZodSchema.optional(), // summer 2024
        kickerColor: hexColorZodSchema.optional(), // summer 2024
        // titleSize: pageBlockTitleSizeZodSchema, // summer 2024
        textShadow: z.boolean(),
        textColor: hexColorZodSchema.optional(),
        textAlignment: hzAlignmentZodSchema.optional(), // summer 2024

        // Call to action
        // Can't place it at designers desired position
        // // ctaButtonEnabled: z.boolean().optional(), // summer 2024
        // // ctaButtonText: translatableZodSchema.optional(), // summer 2024
        // // ctaButtonUrl: z.string().optional(), // summer 2024
        // // ctaButtonTextColor: hexColorZodSchema.optional(), // summer 2024
        // // ctaButtonBackgroundColor: hexColorZodSchema.optional(), // summer 2024
        // // ctaButtonBorderColor: hexColorZodSchema.optional(), // summer 2024

        // General settings
        // multiViewType: contentAutoMultiViewTypeZodSchema.optional(), // summer 2024, deprecated
        layout: contentAutoPageBlockLayoutTypeZodSchema.optional(), // summer 2024, renamed MultiViewType
        style: contentAutoPageBlockStyleTypeZodSchema.optional(), // summer 2024

        // Content
        contentIds: z.array(contentIdZodSchema),
        preferredContentSize: blockContentSizeZodSchema,
        showInactiveContent: z.boolean().optional(),
        randomizeOrderOfContent: z.boolean().optional(), // summer 2024

        // Background
        backgroundImage: z
            .union([
                storageItemImageReferenceZodSchema,
                storageItemImageIdZodSchema,
            ])
            .optional(),
    }),
);

type ContentAutoPageBlock = z.infer<typeof contentAutoPageBlockZodSchema>;

const contentAutoPageBlockIcon = faCubes;
type ContentAutoPageBlockParams = Except<
    ContentAutoPageBlock,
    "_id" | "type" | "_type" | "_createdAt"
>;
const contentAutoPageBlockFactory = (
    params: ContentAutoPageBlockParams,
): ContentAutoPageBlock => {
    return {
        ...params,
        _id: pageBlockIdFactory(),
        _type: "PAGE_BLOCK",
        _createdAt: nowIsoAndUnix(),
        type: "CONTENT_AUTO_PAGE_BLOCK",
        layout: params.layout ?? "auto",
        style: params.style ?? "cards",
        subtitle: asTranslatableValue(params.subtitle),
        title: asTranslatableValue(params.title),
        kicker: params.kicker ? asTranslatableValue(params.kicker) : undefined,
        textAlignment: params.textAlignment || "auto",
        showInactiveContent: params.showInactiveContent ?? false,
    };
};

const contentAutoPageBlockType = "CONTENT_AUTO_PAGE_BLOCK";

const isContentAutoPageBlock = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    input: any,
): input is ContentAutoPageBlock => {
    return (
        input._type === "PAGE_BLOCK" && input.type === "CONTENT_AUTO_PAGE_BLOCK"
    );
};

export {
    contentAutoPageBlockFactory,
    contentAutoPageBlockIcon,
    contentAutoPageBlockType,
    contentAutoPageBlockZodSchema,
    isContentAutoPageBlock,
};
export type { ContentAutoPageBlock };
